import React from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Breadcrumbs,
    Link,
    Card,
    CardContent,
    Grid,
    Divider
} from '@mui/material';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';
import { detectPlatform } from '../../utils/platformDetection';
import { downloadLinkFor, trackStoreClick } from '../../utils/appStoreLinks';
import { blogPosts } from '../../content/blogPosts';
import {
    ArrowBack,
    AccessTime,
    Person,
    Share,
    Favorite,
    BookmarkBorder,
    Psychology,
    Security,
    Group,
    TrendingUp,
    EmojiObjects
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';


const getCategoryIcon = (category: string) => {
    switch (category) {
        case 'Dating Advice':
            return <Favorite sx={{ color: VIBRANT_RED, fontSize: 20 }} />;
        case 'Safety':
            return <Security sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
        case 'Mental Health':
            return <Psychology sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
        case 'Trends':
            return <TrendingUp sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
        case 'Relationships':
            return <Group sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
        default:
            return <EmojiObjects sx={{ color: LIGHT_PURPLE, fontSize: 20 }} />;
    }
};

const BlogPostScreen: React.FC = () => {
    const navigate = useNavigate();
    const { platform } = detectPlatform();
    const download = downloadLinkFor(platform, 'page');
    const { postId } = useParams<{ postId: string }>();
    
    const post = blogPosts.find(blogPost => blogPost.id === postId) ?? null;

    if (!post) {
        return (
            <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                    Article Not Found
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Sorry, we couldn't find the article you're looking for.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/blog')}
                    sx={{ bgcolor: LIGHT_PURPLE }}
                >
                    Back to Blog
                </Button>
            </Container>
        );
    }

    return (
        <>
            <SEOHead
                title={`${post.title} - Pop The Balloon Blog`}
                description={post.excerpt}
                canonical={`https://www.poptheballoonapp.com/blog/${post.id}`}
                ogTitle={post.title}
                ogDescription={post.excerpt}
                ogUrl={`https://www.poptheballoonapp.com/blog/${post.id}`}
            />

            <SiteHeader />
            <Container maxWidth="md" sx={{ py: 4 }}>
                {/* Breadcrumbs */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                    <Link
                        color="inherit"
                        href="/"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/');
                        }}
                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Home
                    </Link>
                    <Link
                        color="inherit"
                        href="/blog"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/blog');
                        }}
                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Blog
                    </Link>
                    <Typography color="text.primary">{post.title}</Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigate('/blog')} sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getCategoryIcon(post.category)}
                            <Chip
                                label={post.category}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(167, 111, 255, 0.1)',
                                    color: LIGHT_PURPLE
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2, lineHeight: 1.2 }}>
                        {post.title}
                    </Typography>

                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.4 }}>
                        {post.excerpt}
                    </Typography>

                    {/* Article Meta */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        py: 2,
                        borderTop: '1px solid rgba(0,0,0,0.1)',
                        borderBottom: '1px solid rgba(0,0,0,0.1)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {post.author}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {post.readTime}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {new Date(post.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" sx={{ color: LIGHT_PURPLE }}>
                                <BookmarkBorder />
                            </IconButton>
                            <IconButton size="small" sx={{ color: LIGHT_PURPLE }}>
                                <Share />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Tags */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                        {post.tags.map((tag) => (
                            <Chip
                                key={tag}
                                label={`#${tag}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor: LIGHT_PURPLE,
                                    color: LIGHT_PURPLE,
                                    '&:hover': {
                                        bgcolor: 'rgba(167, 111, 255, 0.1)'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Article Content */}
                <Card sx={{ mb: 4 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box
                            sx={{
                                '& h2': {
                                    color: LIGHT_PURPLE,
                                    fontWeight: 600,
                                    fontSize: '1.75rem',
                                    mb: 2,
                                    mt: 3
                                },
                                '& h3': {
                                    color: '#333',
                                    fontWeight: 600,
                                    fontSize: '1.5rem',
                                    mb: 2,
                                    mt: 3
                                },
                                '& h4': {
                                    color: '#333',
                                    fontWeight: 600,
                                    fontSize: '1.25rem',
                                    mb: 1.5,
                                    mt: 2
                                },
                                '& p': {
                                    fontSize: '1.1rem',
                                    lineHeight: 1.7,
                                    mb: 2,
                                    color: 'text.primary'
                                },
                                '& ul': {
                                    mb: 2,
                                    pl: 3
                                },
                                '& li': {
                                    fontSize: '1.1rem',
                                    lineHeight: 1.6,
                                    mb: 1,
                                    color: 'text.primary'
                                },
                                '& strong': {
                                    color: LIGHT_PURPLE,
                                    fontWeight: 600
                                },
                                '& blockquote': {
                                    borderLeft: `4px solid ${LIGHT_PURPLE}`,
                                    pl: 2,
                                    py: 1,
                                    bgcolor: 'rgba(167, 111, 255, 0.05)',
                                    borderRadius: '0 4px 4px 0',
                                    mb: 2
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />
                    </CardContent>
                </Card>

                {/* Call to Action */}
                <Card sx={{ bgcolor: 'rgba(167, 111, 255, 0.05)', border: `1px solid rgba(167, 111, 255, 0.2)` }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: LIGHT_PURPLE }}>
                            Ready to Put This Advice into Practice?
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: '500px', mx: 'auto' }}>
                            Join Pop The Balloon and experience authentic dating with people who value genuine connections.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Favorite />}
                                href={download.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackStoreClick(download.store, 'page', platform)}
                                sx={{
                                    bgcolor: LIGHT_PURPLE,
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                        bgcolor: '#8A5CF5',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(167, 111, 255, 0.3)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Join Pop The Balloon
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => navigate('/blog')}
                                sx={{
                                    borderColor: LIGHT_PURPLE,
                                    color: LIGHT_PURPLE,
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                        borderColor: '#8A5CF5',
                                        bgcolor: 'rgba(167, 111, 255, 0.1)'
                                    }
                                }}
                            >
                                Read More Articles
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                <Divider sx={{ my: 4 }} />

                {/* Related Articles Section */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        Continue Reading
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Explore more articles about dating, relationships, and building authentic connections.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/blog')}
                        sx={{
                            borderColor: LIGHT_PURPLE,
                            color: LIGHT_PURPLE,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderColor: '#8A5CF5',
                                bgcolor: 'rgba(167, 111, 255, 0.1)'
                            }
                        }}
                    >
                        View All Articles
                    </Button>
                </Box>
            </Container>
            <AppFooter />
        </>
    );
};

export default BlogPostScreen;
